import { useEffect, useRef, useState } from "react";

export interface IvScorePopupPosition {
  top: number;
  left: number;
  maxHeight: number;
}

const WIDTH = 320;
const MARGIN = 8;
const MIN_HEIGHT = 220;
const MIN_VISIBLE_HEIGHT = 80;

export function useIvScoreTooltip(open: boolean) {
  const triggerRef = useRef<HTMLElement | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [position, setPosition] = useState<IvScorePopupPosition | null>(null);

  useEffect(() => {
    if (!open || !triggerRef.current) {
      setPosition(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    const footerTop =
      document
        .querySelector(".fintheon-footer-surface")
        ?.getBoundingClientRect().top ?? window.innerHeight;
    const bottomLimit = Math.max(
      MARGIN + MIN_VISIBLE_HEIGHT,
      Math.min(footerTop, window.innerHeight) - MARGIN,
    );
    const left = Math.max(
      MARGIN,
      Math.min(rect.right - WIDTH, window.innerWidth - WIDTH - MARGIN),
    );
    const belowTop = rect.bottom + 4;
    const aboveTop = rect.top - MIN_HEIGHT - 4;
    const top =
      belowTop + MIN_HEIGHT <= bottomLimit
        ? belowTop
        : Math.max(MARGIN, Math.min(aboveTop, bottomLimit - MIN_HEIGHT));
    setPosition({
      top,
      left,
      maxHeight: Math.max(MIN_VISIBLE_HEIGHT, bottomLimit - top),
    });
  }, [open]);

  useEffect(
    () => () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
    },
    [],
  );

  const show = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = null;
  };
  const queueHide = (close: () => void) => {
    hideTimer.current = setTimeout(close, 150);
  };
  return { position, queueHide, show, triggerRef };
}
